import { createFileRoute, Link, linkOptions } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { formatDateOnly, MOVE_IN_STAGE_LABELS, type MoveInStage } from "@alm/domain";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { AmyThread } from "@/components/amy/amy-thread";
import { liveRefresh } from "@/components/amy/live-refresh";
import { FamilyPortalCard } from "@/components/amy/family-portal-card";
import { MoveInAllSteps } from "@/components/amy/move-in-all-steps";
import { MoveInTasks } from "@/components/amy/move-in-tasks";
import { MoveInFile } from "@/components/amy/move-in-file";
import { StandingDisclosure } from "@/components/amy/standing-disclosure";
import { useMoveInActions } from "@/components/amy/use-move-in-actions";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowRight } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useZone } from "@/lib/facility/zone";

export const Route = createFileRoute("/_authenticated/ws/amy/$moveInId")({
  head: () => ({ meta: [{ title: "Move-in | Amy | People AI" }] }),
  component: MoveInDetail,
});

/** One move-in in Amy's workspace: the conversation with Amy in the middle, where it stands
 * beside it, and the full board only when someone asks for it. */
function MoveInDetail() {
  const { moveInId } = Route.useParams();
  const moveIn = useQuery({
    ...orpc.agents.amy.moveIns.get.queryOptions({ input: { moveInId } }),
    ...liveRefresh,
  });
  const zone = useZone(moveIn.data?.facilityId ?? null);
  const actions = useMoveInActions();
  const [allSteps, setAllSteps] = useState(false);

  if (moveIn.isPending) return <Skeleton className="h-96 w-full" />;
  if (moveIn.isError) {
    return (
      <ReadFailed
        message="Could not load this move-in."
        error={moveIn.error}
        onRetry={() => void moveIn.refetch()}
        retrying={moveIn.isFetching}
      />
    );
  }
  const data = moveIn.data;
  const facilityId = data.facilityId;
  const name = `${data.firstName} ${data.lastName}`;
  const open = data.status === "open";
  const stage = data.discharged ? "Discharged" : MOVE_IN_STAGE_LABELS[data.stage as MoveInStage];
  const date = data.moveInDate ? formatDateOnly(data.moveInDate, zone) : null;
  const share = data.summary.total ? (data.summary.complete / data.summary.total) * 100 : 0;
  const ended = open
    ? null
    : `${data.status === "cancelled" ? "Stopped" : data.closeKind === "forced" ? "Force-closed" : "Closed"} ${formatDateOnly(data.endedAt, zone)}${data.endReason ? `: ${data.endReason}` : ""}`;
  const openSteps = data.steps
    .filter((step) => step.status !== "complete" && step.status !== "waived")
    .map((step) => step.title);
  const panel = facilityId ? (
    <MoveInFile data={data} facilityId={facilityId} onAllSteps={() => setAllSteps(true)} />
  ) : null;
  const record = data.residentId
    ? linkOptions({ to: "/residents/$residentId", params: { residentId: data.residentId } })
    : data.leadId
      ? linkOptions({ to: "/crm/leads/$leadId", params: { leadId: data.leadId } })
      : null;
  const shelby = record ? (
    <Link {...record}>
      <span className="sm:hidden">Shelby</span>
      <span className="hidden sm:inline">Full record in Shelby</span>
      <ArrowRight className="size-4" />
    </Link>
  ) : null;

  return (
    <div className="flex flex-col md:h-[calc(100svh-3.5rem-var(--dock-h,0px))]">
      <header className="bg-card flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-3 md:px-6">
        <Button asChild variant="ghost" size="icon" className="-ms-2 hidden size-8 md:inline-flex">
          <Link to="/ws/amy" aria-label="Back to Amy">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h1 className="truncate text-lg font-semibold tracking-tight">{name}</h1>
          <span className="bg-primary/10 text-primary rounded-xs px-2.5 py-0.5 text-xs">
            {date ? `Moving in ${date}` : "Date to be decided"}
          </span>
          <span className="bg-muted text-muted-foreground rounded-xs px-2.5 py-0.5 text-xs">
            {[stage, data.pathway === "medicaid" ? "Medicaid" : null, data.facilityName]
              .filter(Boolean)
              .join(", ")}
          </span>
          {ended ? <span className="text-muted-foreground text-xs">{ended}</span> : null}
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <Progress value={share} aria-label="Steps done" className="h-1.5 w-28" />
          <span className="text-muted-foreground text-xs tabular-nums">
            {`${String(data.summary.complete)} of ${String(data.summary.total)}`}
          </span>
        </div>
        {shelby ? (
          <Button asChild variant="link" className="px-0">
            {shelby}
          </Button>
        ) : null}
        <RowActions
          label={name}
          items={actions.actionsFor({
            ...data,
            progress: { requiredLeft: data.blockingClose.length },
          })}
        />
      </header>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b p-3 lg:hidden">
            <StandingDisclosure label="The file">{panel}</StandingDisclosure>
          </div>
          <div className="mx-auto flex h-[72svh] w-full max-w-[820px] min-w-0 flex-col px-4 pt-2 pb-4 md:h-auto md:min-h-0 md:flex-1 md:px-8">
            {facilityId ? (
              <AmyThread
                key={moveInId}
                moveInId={moveInId}
                facilityId={facilityId}
                welcome={{ firstName: data.firstName, openSteps }}
                leadId={data.residentId ? null : data.leadId}
              />
            ) : null}
          </div>
        </div>
        <aside
          aria-label="The move-in's file"
          className="bg-card hidden w-[380px] shrink-0 overflow-y-auto border-l p-5 lg:block"
        >
          {panel}
        </aside>
      </div>
      <Sheet open={allSteps && Boolean(facilityId)} onOpenChange={setAllSteps}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetTitle>{`${name}'s steps`}</SheetTitle>
          <SheetDescription className="sr-only">Every step of the move-in.</SheetDescription>
          {facilityId ? (
            <div id="move-in-all-steps" className="flex flex-col gap-5 pt-2">
              <MoveInAllSteps
                data={data}
                dateNote={date ? `Move-in ${date}` : "Move-in date to be determined"}
              />
              <MoveInTasks
                moveInId={moveInId}
                facilityId={facilityId}
                steps={data.steps}
                open={open}
              />
              <FamilyPortalCard
                moveInId={moveInId}
                facilityId={facilityId}
                steps={data.steps}
                open={open}
              />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
      {actions.dialog}
    </div>
  );
}
